// The jobs table (Studio overview and Jobs), its counts cell and the cancel step.

import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useCancelJob } from "../../api/studio";
import type { StudioJob } from "../../api/studioSchemas";
import { useCan } from "../../auth/AuthContext";
import { tableStyles } from "../../components/table";
import { Button, ConfirmDialog, Skeleton } from "../../components/ui";
import { formatDateTime } from "../../lib/dates";
import { fmtCompact, fmtMs, typeSlug } from "../../lib/format";
import { fmtUsd, isActiveJob, splitJobError, STUDIO_ERRORS } from "../../lib/studio";
import pages from "../pages.module.css";
import { JobStatusChip, KindChip } from "./components";
import s from "./studio.module.css";

/** Requested · created · failed verification · needs review, and how far the job has got. */
export function JobCounts({ job }: { job: StudioJob }) {
  const { requested, created, failedVerification, needsReview } = job.counts;
  const done = created + failedVerification + needsReview;
  const active = isActiveJob(job.status);
  return (
    <div data-testid="job-counts">
      <div className={s.counts}>
        <span data-k="requested" title="Candidates asked for (not puzzles guaranteed)">
          {requested}
          <small>requested</small>
        </span>
        <span data-k="created" title="Solver-verified bank drafts">
          {created}
          <small>created</small>
        </span>
        <span data-k="failed" data-nonzero={failedVerification > 0 || undefined} title="Failed verification (or duplicates)">
          {failedVerification}
          <small>failed</small>
        </span>
        <span data-k="review" data-nonzero={needsReview > 0 || undefined} title="No solver: a person must approve these">
          {needsReview}
          <small>review</small>
        </span>
      </div>
      {job.kind !== "import" ? (
        <span className={s.progress} data-active={active || undefined} role="progressbar" aria-valuemin={0} aria-valuemax={requested} aria-valuenow={done} aria-label="Candidates processed">
          <span style={{ width: `${requested ? Math.min(100, (done / requested) * 100) : 0}%` }} />
        </span>
      ) : null}
    </div>
  );
}

/** How far a job has got: candidates processed of those requested. */
export function JobProgress({ job }: { job: StudioJob }) {
  const { requested, created, failedVerification, needsReview } = job.counts;
  const done = created + failedVerification + needsReview;
  return (
    <span className={s.progress} data-wide data-active={isActiveJob(job.status) || undefined} role="progressbar" aria-valuemin={0} aria-valuemax={requested} aria-valuenow={done} aria-label="Candidates processed">
      <span style={{ width: `${requested ? Math.min(100, (done / requested) * 100) : 0}%` }} />
    </span>
  );
}

export const jobDuration = (job: StudioJob) =>
  job.startedAt ? fmtMs((job.finishedAt ? Date.parse(job.finishedAt) : Date.now()) - Date.parse(job.startedAt)) : "—";

/** Cancel (editor+): a queued job at once, a running one after its current batch. */
export function CancelJob({ job, size = "sm" }: { job: StudioJob; size?: "sm" | "md" }) {
  const canEdit = useCan("studio.edit");
  const mutation = useCancelJob();
  const [open, setOpen] = useState(false);
  if (!canEdit || !isActiveJob(job.status) || job.cancelRequested) return null;
  return (
    <>
      <Button
        size={size}
        variant="danger"
        onClick={() => {
          mutation.reset();
          setOpen(true);
        }}
      >
        Cancel
      </Button>
      <ConfirmDialog
        open={open}
        title="Cancel this job?"
        confirmLabel="Cancel job"
        tone="danger"
        busy={mutation.isPending}
        error={mutation.error}
        onCancel={() => setOpen(false)}
        onConfirm={() => mutation.mutate(job.id, { onSuccess: () => setOpen(false) })}
      >
        <p>
          {job.status === "queued" ? "It hasn't started, so it stops now." : "It is running: it stops after its current batch."} Drafts already made stay in the bank.
        </p>
        <p className={pages.faint}>
          <span className="mono">{job.type}</span> · {job.counts.requested} requested
        </p>
      </ConfirmDialog>
    </>
  );
}

/** `compact` (the Studio overview) drops tokens, requester and duration. */
export function JobsTable({ jobs, loading, rows = 6, compact = false }: { jobs: StudioJob[] | undefined; loading: boolean; rows?: number; compact?: boolean }) {
  const navigate = useNavigate();
  const cols = compact ? 5 : 9;
  return (
    <table className={tableStyles.table} data-testid="jobs">
      <thead>
        <tr>
          <th>Status</th>
          <th>Job</th>
          <th>Counts</th>
          {compact ? null : <th data-align="right">Tokens in / out</th>}
          <th data-align="right">Cost</th>
          {compact ? null : <th>Requested by</th>}
          {compact ? null : <th>Created</th>}
          {compact ? null : <th data-align="right">Took</th>}
          <th />
        </tr>
      </thead>
      <tbody>
        {loading || !jobs
          ? Array.from({ length: rows }, (_, i) => (
              <tr key={i}>
                {Array.from({ length: cols }, (__, j) => (
                  <td key={j}>
                    <Skeleton height={12} width={j === 1 ? 140 : 60} />
                  </td>
                ))}
              </tr>
            ))
          : jobs.map((job) => {
              const err = splitJobError(job.error);
              return (
                <tr
                  key={job.id}
                  data-clickable
                  data-job={job.id}
                  data-status={job.status}
                  onClick={(e) => {
                    // Clicks from a portal (the cancel dialog) bubble through React but aren't inside the row.
                    if (!e.currentTarget.contains(e.target as Node) || (e.target as HTMLElement).closest("a,button")) return;
                    navigate(`/studio/jobs/${job.id}`);
                  }}
                >
                  <td>
                    <JobStatusChip status={job.status} cancelRequested={job.cancelRequested} />
                  </td>
                  <td>
                    <div className={s.jobType}>
                      <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <KindChip kind={job.kind} />
                        <Link to={`/studio/jobs/${job.id}`} className={tableStyles.id}>
                          {job.kind === "import" ? "curated import" : job.kind === "levelpack" ? `${job.type} packs` : typeSlug(job.type)}
                        </Link>
                      </span>
                      <small>
                        {job.type} · {job.id.slice(0, 8)}
                        {job.params.theme ? ` · “${job.params.theme}”` : ""}
                      </small>
                      {err ? (
                        <span className={s.errorLine} title={job.error ?? ""}>
                          {err.code ? (STUDIO_ERRORS[err.code]?.title ?? err.code) : err.message}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td>
                    <JobCounts job={job} />
                  </td>
                  {compact ? null : (
                    <td data-align="right" className={tableStyles.mono}>
                      {job.kind === "ai" ? `${fmtCompact(job.tokens.input)} / ${fmtCompact(job.tokens.output)}` : <span className={tableStyles.faint}>—</span>}
                    </td>
                  )}
                  <td data-align="right" className={tableStyles.mono}>
                    {job.kind === "ai" ? fmtUsd(job.costUsd) : <span className={tableStyles.faint}>free</span>}
                  </td>
                  {compact ? null : (
                    <>
                      <td className={tableStyles.mono}>{job.requestedBy ?? <span className={tableStyles.faint}>CLI</span>}</td>
                      <td className={tableStyles.dim}>{formatDateTime(job.createdAt)}</td>
                      <td data-align="right" className={tableStyles.mono}>
                        {jobDuration(job)}
                      </td>
                    </>
                  )}
                  <td data-align="right">
                    <CancelJob job={job} />
                  </td>
                </tr>
              );
            })}
      </tbody>
    </table>
  );
}

export function LiveBadge({ live }: { live: boolean }) {
  return (
    <span className={s.live} data-live={live || undefined} data-testid="live-badge" title={live ? "Refreshing every 3 s while a job is queued or running" : "No job is running"}>
      <span className={s.liveDot} aria-hidden />
      {live ? "Live · every 3 s" : "Idle"}
    </span>
  );
}
